<script setup lang="ts">
import { ChevronDownIcon } from "@heroicons/vue/20/solid";
import { computed, onBeforeUnmount, onMounted, ref, toRefs } from "vue";
import ExternalLink from "@/components/svg-icons/ExternalLink.vue";
import type { ReminderItemProps } from "./types.ts";

const props = defineProps<ReminderItemProps>();
const { item, index } = toRefs(props);

const isExpanded = ref(false);
const isOverflowing = ref(false);
const bodyElement = ref<HTMLElement | null>(null);

const hasProtocol = (link: string) => /^[a-z][a-z\d+.-]*:/i.test(link);

const normalizeHref = (href: string) => {
  const trimmedHref = href.trim();

  if (!trimmedHref) return "";

  if (hasProtocol(trimmedHref) || trimmedHref.startsWith("//")) {
    return trimmedHref;
  }

  return `https://${trimmedHref}`;
};

const checkOverflowing = () => {
  if (!bodyElement.value) return;
  isOverflowing.value = bodyElement.value.scrollHeight > bodyElement.value.clientHeight;
};

const externalLink = computed<string>(() => {
  if (!item.value.external_link_url) return "";

  return normalizeHref(item.value.external_link_url);
});

const normalizeBodyHtml = computed<string>(() => {
  if (!item.value.body_html) return "";

  const normalizedHtml = item.value.body_html.replace(/<a\s+([^>]*?)href=["']([^"']+)["']/gi, (match, attrs, href) => {
    const normalizedHref = normalizeHref(href);

    return `<a ${attrs}href="${normalizedHref}" target="_blank"`;
  });

  return normalizedHtml;
});

onMounted(() => {
  checkOverflowing();
  window.addEventListener("resize", checkOverflowing);
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", checkOverflowing);
});
</script>

<template>
  <div class="flex flex-col px-6 pb-4" :class="{ 'border-t border-gray-200 pt-4': index !== 0 }">
    <span class="font-bold text-sm pb-2">{{ item.header }}</span>
    <div class="flex items-center gap-2">
      <span ref="bodyElement" id="reminder-body-html" class="text-sm flex-1"  :class="{ 'line-clamp-4': !isExpanded }" v-html="normalizeBodyHtml"></span>
      <a
        id="external-link"
        v-if="item.external_link_url"
        class="flex-shrink-0 pt-2"
        :href="externalLink"
        target="_blank"
      >
        <ExternalLink/>
      </a>

    </div>
    <button v-if="isOverflowing" @click="isExpanded = !isExpanded" id="read-more-less-button" class="mx-auto flex items-center gap-1 text-sm text-teal-900 font-medium">
      {{isExpanded ? 'Read Less' : 'Read More'}}
      <ChevronDownIcon class="w-6 h-6" :class="{ 'rotate-180': isExpanded }"/>
    </button>
  </div>
</template>

<style scoped>
#reminder-body-html :deep(a) {
  font-weight: 600;
  text-decoration: underline;
  color: #007f80;
}
</style>
